import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Divider } from "@mui/material";
import UpdatePassword from "./SignInMethods/UpdatePassword";
import Passkeys from "./SignInMethods/Passkeys";
import AuthenticatorApp from "./SignInMethods/AuthenticatorApp";
import PreferredSignInMethod from "./PreferredSignInMethod";

// English fallback copy for portals (School) that don't initialize
// react-i18next namespaces for this surface. Keep in sync with the
// "accountSecurity" block in public/locales/en/common.json.
const EN = {
  title: "Account Security",
  description: "Here you can manage your account security settings.",
  signInMethods: "Sign in methods",
};

// Shared across School (staff), Guardian, and Student self-service settings.
// Callers pass a `user` object exposing the login email as `.email` -- for
// School that's staff.school_email, not staff.email (a separate personal
// address), so School maps it explicitly at the call site.
//
// `translate` gates react-i18next: School doesn't set up translation for its
// portal, so it passes translate={false} to get plain English copy instead
// of untranslated/broken keys. Guardian and Student default to translated.
export default function AccountSecurity({ user, translate = true }) {
  const { t: i18nT } = useTranslation("common", {
    keyPrefix: "accountSecurity",
  });
  const t = (key) => (translate ? i18nT(key) : EN[key]);
  const helperTextStyle = {
    color: "#6B7280",
  };

  // Passkeys and AuthenticatorApp each own their own "configured" status --
  // neither knows when the other changes. Bumping this on any change that
  // could flip either method's configured state tells PreferredSignInMethod
  // to re-check eligibility immediately, instead of only on next mount.
  const [signInMethodsVersion, setSignInMethodsVersion] = useState(0);
  const bumpSignInMethodsVersion = () =>
    setSignInMethodsVersion((version) => version + 1);

  return (
    <Box>
      <Box>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            fontWeight: "normal",
          }}
        >
          {t("signInMethods")}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ width: user.type !== "staff" ? "100%" : "60%" }}>
          {user.type !== "student" && (
            <UpdatePassword
              user={user}
              helperTextStyle={helperTextStyle}
              translate={translate}
            />
          )}
          <Passkeys
            helperTextStyle={helperTextStyle}
            translate={translate}
            onChange={bumpSignInMethodsVersion}
          />
          <AuthenticatorApp
            helperTextStyle={helperTextStyle}
            translate={translate}
            onChange={bumpSignInMethodsVersion}
          />
        </Box>
      </Box>

      <PreferredSignInMethod
        helperTextStyle={helperTextStyle}
        translate={translate}
        containerSx={{ width: user.type !== "staff" ? "100%" : "60%" }}
        refreshToken={signInMethodsVersion}
      />
    </Box>
  );
}
